import { Icon as IconifyIcon } from '@iconify/react';
import { styled } from '@linaria/react';
import type { KeyboardEvent } from 'react';
import { useCallback, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useNavigate, useOutletContext, useParams } from 'react-router';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import {
  useAutomationWorkflow,
  useAutomationWorkflowRuns,
  useRunAutomationWorkflow,
} from '../../../../api/automations-workflows-client/automations-workflows-client.hooks';
import {
  type AutomationWorkflowRun,
  AutomationRunStatus,
  AutomationTriggerType,
} from '../../../../api/automations-workflows-client/automations-workflows-client.type';
import { toast } from '../../../../containers/notifications';
import { Button } from '../../../../ui-library/button/button';
import { Icon } from '../../../../ui-library/icon/icon';
import { Loader } from '../../../../ui-library/loader/loader';
import { Text } from '../../../../ui-library/typography/typography';
import type { FloraAgentOutletContext } from '../../flora-agent';
import { AgentViewHeader } from '../agent-view-header';
import {
  formatDuration,
  formatRunTimeAbsolute,
  formatRunTimeRelative,
  getDeliveryAttemptTargetLabel,
} from '../shared/schedule.helpers';
import {
  automationRunDurationSeconds,
  automationRunHasDetails,
  automationRunStatusPillStyle,
  getAutomationPrimarySlackDeepLink,
  groupAutomationRunsByDate,
  isAutomationSlackOnlyRun,
} from './agent-automation-runs.helpers';
import { AgentAutomationRunChatDrawer } from './agent-automation-run-chat-drawer';
import { getTriggerDescription } from './agent-automations.helpers';

const AUTOMATIONS_PATH = '/application/flora-agent/automations';

// Exported so tests assert against the exact glyphs (these use a typographic
// apostrophe, U+2019) rather than re-typing copy that would silently mismatch.
const EMPTY_RUNS_MANUAL_COPY = 'Trigger a manual run or wait for the next scheduled execution.';
export const EMPTY_RUNS_EVENT_COPY = 'Runs appear here once this automation’s event fires.';
export const RUNS_ERROR_COPY = 'Couldn’t load run history. Refresh to try again.';
export const NOT_FOUND_COPY = 'Automation not found';

/**
 * Runs/detail view for a single automation workflow: shows aggregate run stats
 * and the full run history, with affordances to refresh, trigger a manual run,
 * and jump to the edit page. Workflows the user doesn't own resolve to a
 * not-found state because the API scopes both fetches to the owner.
 *
 * @returns {JSX.Element} The automation runs detail view
 */
export function AgentAutomationRunsView() {
  const navigate = useNavigate();
  const agentContext = useOutletContext<FloraAgentOutletContext | null>();
  const { workflowId } = useParams<{ workflowId: string }>();
  const { workflow, isLoading: workflowLoading } = useAutomationWorkflow(workflowId);
  const {
    runs,
    isLoading: runsLoading,
    error: runsError,
    refetch: refetchRuns,
  } = useAutomationWorkflowRuns(workflowId);
  const runMutation = useRunAutomationWorkflow();

  const [expandedRunId, setExpandedRunId] = useState<string | null>(null);
  const [openRunChatId, setOpenRunChatId] = useState<string | null>(null);
  const [isRefreshingManually, setIsRefreshingManually] = useState(false);

  const summary = useMemo(() => {
    const total = runs.length;
    const succeeded = runs.filter((run) => run.status === AutomationRunStatus.COMPLETED).length;
    const failed = runs.filter((run) => run.status === AutomationRunStatus.FAILED).length;
    const durations = runs
      .map(automationRunDurationSeconds)
      .filter((duration): duration is number => duration !== null);
    const avgDuration = durations.length ? durations.reduce((sum, value) => sum + value, 0) / durations.length : null;
    return { total, succeeded, failed, avgDuration };
  }, [runs]);

  const groups = useMemo(() => groupAutomationRunsByDate(runs), [runs]);
  const openRunChat = useMemo(
    () => agentContext?.chatHistories.find((history) => history.id === openRunChatId),
    [agentContext?.chatHistories, openRunChatId],
  );
  const refreshChatHistories = agentContext?.refreshChatHistories;

  const canRunManually = workflow ? workflow.trigger_type !== AutomationTriggerType.EVENT && workflow.is_active : false;

  const handleBack = useCallback(() => {
    navigate(AUTOMATIONS_PATH);
  }, [navigate]);

  const handleRunNow = useCallback(() => {
    if (!workflow) return;
    runMutation.mutate(workflow.id, {
      onSuccess: () => toast.success(`Queued a run for "${workflow.name}"`),
      onError: (error) => toast.error(error.message || 'Failed to trigger run'),
    });
  }, [workflow, runMutation]);

  const handleRefresh = useCallback(() => {
    if (isRefreshingManually) return;
    setIsRefreshingManually(true);
    void Promise.resolve(refetchRuns()).finally(() => setIsRefreshingManually(false));
  }, [isRefreshingManually, refetchRuns]);

  const handleOpenEdit = useCallback(() => {
    if (!workflowId) return;
    navigate(`${AUTOMATIONS_PATH}/${workflowId}/edit`);
  }, [navigate, workflowId]);

  const toggleExpanded = useCallback((runId: string) => {
    setExpandedRunId((current) => (current === runId ? null : runId));
  }, []);

  const handleOpenRunChat = useCallback(
    (chatId: string) => {
      setOpenRunChatId(chatId);
      refreshChatHistories?.();
    },
    [refreshChatHistories],
  );

  const handleOpenFullRunChat = useCallback(() => {
    if (openRunChatId) navigate(`/application/flora-agent/${openRunChatId}`);
  }, [navigate, openRunChatId]);

  if (workflowLoading || runsLoading) {
    return (
      <Container>
        <LoadingWrapper>
          <Loader />
        </LoadingWrapper>
      </Container>
    );
  }

  if (!workflow) {
    return (
      <Container>
        <EmptyState>
          <Icon name="error" color="var(--ink-lighter)" size={48} />
          <Text size="regular" color="var(--ink-lighter)" style={{ marginTop: 12 }}>
            {NOT_FOUND_COPY}
          </Text>
          <Button variant="outline" size="sm" onClick={handleBack} style={{ marginTop: 16 }}>
            Back to Automations
          </Button>
        </EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      <MainColumn>
        <TopNav>
          <Button variant="outline" size="sm" onClick={handleBack}>
            <Icon name="arrow_back" color="var(--ink-dark)" size={16} style={{ marginRight: 6, cursor: 'pointer' }} />
            Automations
          </Button>
        </TopNav>

        <HeaderRow>
          <AgentViewHeader title={workflow.name} subtitle={getTriggerDescription(workflow)} />
          <HeaderActions>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              loading={isRefreshingManually}
              aria-label="Refresh runs"
            >
              <Icon name="refresh" color="var(--ink-dark)" size={16} style={{ marginRight: 4, cursor: 'pointer' }} />
              Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={handleOpenEdit} aria-label="Edit automation">
              <Icon name="edit" color="var(--ink-dark)" size={16} style={{ marginRight: 4, cursor: 'pointer' }} />
              Edit
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleRunNow}
              loading={runMutation.isPending}
              disabled={!canRunManually}
            >
              <Icon name="play_arrow" color="#ffffff" size={16} style={{ marginRight: 4, cursor: 'pointer' }} />
              Run now
            </Button>
          </HeaderActions>
        </HeaderRow>

        {workflow.description && (
          <DescriptionRow>
            <Text size="small" color="var(--ink-base)">
              {workflow.description}
            </Text>
          </DescriptionRow>
        )}

        <SummaryGrid>
          <SummaryCard>
            <SummaryHead>
              <Icon name="play_circle" color="var(--ink-lighter)" size={16} />
              <SummaryLabel>Total runs</SummaryLabel>
            </SummaryHead>
            <SummaryValue>{summary.total}</SummaryValue>
          </SummaryCard>
          <SummaryCard>
            <SummaryHead>
              <Icon name="check_circle" color="#23c16b" size={16} />
              <SummaryLabel>Succeeded</SummaryLabel>
            </SummaryHead>
            <SummaryValue>
              {summary.succeeded}
              {summary.total > 0 && (
                <SummaryPercent>{Math.round((summary.succeeded / summary.total) * 100)}%</SummaryPercent>
              )}
            </SummaryValue>
          </SummaryCard>
          <SummaryCard>
            <SummaryHead>
              <Icon name="error" color="#d3180c" size={16} />
              <SummaryLabel>Failed</SummaryLabel>
            </SummaryHead>
            <SummaryValue>{summary.failed}</SummaryValue>
          </SummaryCard>
          <SummaryCard>
            <SummaryHead>
              <Icon name="bolt" color="#d98e2b" size={16} />
              <SummaryLabel>Avg duration</SummaryLabel>
            </SummaryHead>
            <SummaryValue>{formatDuration(summary.avgDuration)}</SummaryValue>
          </SummaryCard>
        </SummaryGrid>

        {runsError && runs.length === 0 ? (
          <EmptyState>
            <Icon name="error" color="var(--ink-lighter)" size={48} />
            <Text size="regular" color="var(--ink-lighter)" style={{ marginTop: 12 }}>
              {RUNS_ERROR_COPY}
            </Text>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              loading={isRefreshingManually}
              style={{ marginTop: 16 }}
            >
              <Icon name="refresh" color="var(--ink-dark)" size={16} style={{ marginRight: 4, cursor: 'pointer' }} />
              Refresh
            </Button>
          </EmptyState>
        ) : runs.length === 0 ? (
          <EmptyState>
            <Icon name="history" color="var(--ink-lighter)" size={48} />
            <Text size="regular" color="var(--ink-lighter)" style={{ marginTop: 12 }}>
              No runs yet
            </Text>
            <Text size="small" color="var(--ink-lighter)">
              {canRunManually ? EMPTY_RUNS_MANUAL_COPY : EMPTY_RUNS_EVENT_COPY}
            </Text>
          </EmptyState>
        ) : (
          <ScrollArea>
            {groups.map((group) => (
              <GroupSection key={group.label}>
                <GroupLabel>
                  <Text size="tiny" weight="bold" color="var(--ink-lighter)">
                    {group.label.toUpperCase()}
                  </Text>
                </GroupLabel>
                <RunTable>
                  <RunHeaderRow>
                    <RunHeaderCell $flex={1.4}>When</RunHeaderCell>
                    <RunHeaderCell $flex={1}>Status</RunHeaderCell>
                    <RunHeaderCell $flex={1}>Trigger</RunHeaderCell>
                    <RunHeaderCell $flex={1}>Duration</RunHeaderCell>
                    <RunHeaderCell $flex={0.6}>Chat</RunHeaderCell>
                    <RunHeaderCell $flex={0.3} />
                  </RunHeaderRow>
                  {group.runs.map((run) => (
                    <RunRowView
                      key={run.id}
                      run={run}
                      expanded={expandedRunId === run.id}
                      onToggle={() => toggleExpanded(run.id)}
                      onOpenChat={handleOpenRunChat}
                    />
                  ))}
                </RunTable>
              </GroupSection>
            ))}
          </ScrollArea>
        )}
      </MainColumn>
      {openRunChatId && (
        <AgentAutomationRunChatDrawer
          chatHistory={openRunChat}
          onClose={() => setOpenRunChatId(null)}
          onOpenFullChat={handleOpenFullRunChat}
        />
      )}
    </Container>
  );
}

interface RunRowProps {
  run: AutomationWorkflowRun;
  expanded: boolean;
  onToggle: () => void;
  onOpenChat: (chatId: string) => void;
}

function RunRowView({ run, expanded, onToggle, onOpenChat }: RunRowProps) {
  const pill = automationRunStatusPillStyle(run.status);
  const duration = formatDuration(automationRunDurationSeconds(run));
  const when = run.started_at ?? run.metadata_created_at;
  // PENDING/PROCESSING rows can have no output, error, or delivery attempts
  // yet — gate the expand affordance so the drawer is never empty.
  const hasDetails = automationRunHasDetails(run);
  const isExpanded = expanded && hasDetails;

  // When a run was delivered ONLY through Slack, the conversation lives in a
  // Slack thread, so swap the web "View chat" link for an "Open in Slack"
  // deep link. Runs with no delivery attempts keep the chat link as default.
  const slackOnly = isAutomationSlackOnlyRun(run);
  const primarySlackLink = getAutomationPrimarySlackDeepLink(run);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!hasDetails) return;
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onToggle();
    }
  };

  return (
    <>
      <RunRow
        role={hasDetails ? 'button' : undefined}
        tabIndex={hasDetails ? 0 : undefined}
        aria-expanded={hasDetails ? isExpanded : undefined}
        onClick={hasDetails ? onToggle : undefined}
        onKeyDown={handleKeyDown}
        $clickable={hasDetails}
      >
        <RunCell $flex={1.4}>
          <WhenStack>
            <Text size="small" color="var(--ink-dark)">
              {formatRunTimeRelative(when)}
            </Text>
            <Text size="tiny" color="var(--ink-lighter)">
              {formatRunTimeAbsolute(when, 'MMM D, h:mm A')}
            </Text>
          </WhenStack>
        </RunCell>
        <RunCell $flex={1}>
          <StatusPill style={{ color: pill.color, background: pill.background }}>
            <Text size="tiny" weight="medium" color="inherit">
              {pill.label}
            </Text>
          </StatusPill>
        </RunCell>
        <RunCell $flex={1}>
          <Text size="tiny" color="var(--ink-lighter)">
            {run.trigger_source}
          </Text>
        </RunCell>
        <RunCell $flex={1}>
          <Text size="tiny" color="var(--ink-lighter)" style={{ fontFamily: 'var(--font-mono, monospace)' }}>
            {duration}
          </Text>
        </RunCell>
        <RunCell $flex={0.6}>
          {slackOnly && primarySlackLink ? (
            <ChatLinkAnchor
              href={primarySlackLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(event) => event.stopPropagation()}
              aria-label="Open in Slack"
            >
              <IconifyIcon icon="material-symbols:open-in-new" width={14} height={14} />
              Open
            </ChatLinkAnchor>
          ) : run.chat_history ? (
            <ChatLinkButton
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                if (run.chat_history) onOpenChat(run.chat_history);
              }}
              aria-label="Open chat"
            >
              <IconifyIcon icon="material-symbols:open-in-new" width={14} height={14} />
              View
            </ChatLinkButton>
          ) : (
            <Text size="tiny" color="var(--ink-lighter)">
              —
            </Text>
          )}
        </RunCell>
        <RunCell $flex={0.3} style={{ justifyContent: 'flex-end' }}>
          {hasDetails && (
            <IconifyIcon
              icon={isExpanded ? 'material-symbols:expand-less' : 'material-symbols:expand-more'}
              width={18}
              height={18}
            />
          )}
        </RunCell>
      </RunRow>
      {isExpanded && (
        <RunExpanded>
          {run.error ? (
            <ErrorBox>
              <IconifyIcon icon="material-symbols:error-outline" width={16} height={16} />
              <Text size="small" color="var(--ink-dark)" style={{ whiteSpace: 'pre-wrap' }}>
                {run.error}
              </Text>
            </ErrorBox>
          ) : null}
          {run.output_text && (
            <OutputBlock>
              <OutputHeader>
                <Text size="tiny" weight="bold" color="var(--ink-lighter)">
                  OUTPUT
                </Text>
              </OutputHeader>
              <Markdown>
                <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>{run.output_text}</ReactMarkdown>
              </Markdown>
            </OutputBlock>
          )}
          {run.delivery_attempts.length > 0 && (
            <DeliveryList>
              {run.delivery_attempts.map((attempt, index) => {
                const targetLabel = getDeliveryAttemptTargetLabel(attempt);
                return (
                  <DeliveryAttemptBlock key={`${attempt.channel}-${attempt.at}-${index}`}>
                    <DeliveryRow>
                      <Text size="tiny" color="var(--ink-lighter)">
                        {attempt.channel}
                      </Text>
                      <Text size="tiny" color={attempt.status === 'sent' ? 'var(--primary-base)' : 'var(--red-base)'}>
                        {attempt.status}
                      </Text>
                      {targetLabel && (
                        <Text size="tiny" color="var(--ink-lighter)">
                          → {targetLabel}
                        </Text>
                      )}
                    </DeliveryRow>
                    {attempt.status === 'failed' && attempt.error && (
                      <DeliveryError>
                        <Text size="tiny" color="var(--red-base)">
                          {attempt.error}
                        </Text>
                      </DeliveryError>
                    )}
                  </DeliveryAttemptBlock>
                );
              })}
            </DeliveryList>
          )}
        </RunExpanded>
      )}
    </>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: row;
  height: 100%;
  width: 100%;
  min-width: 0;
`;

const MainColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  padding: 24px 0 40px;
`;

const TopNav = styled.div`
  padding: 0 80px;
  margin-bottom: 12px;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding-right: 80px;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 8px;
  padding-right: 0;
`;

const DescriptionRow = styled.div`
  padding: 0 80px 20px;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  padding: 0 80px;
  margin-bottom: 24px;
`;

const SummaryCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 14px 16px;
  border-radius: 10px;
  border: 1px solid var(--sky-light);
  background: var(--base-white);
`;

const SummaryHead = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const SummaryLabel = styled.div`
  font-size: 11px;
  font-weight: 500;
  color: var(--ink-lighter);
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const SummaryValue = styled.div`
  font-size: 22px;
  font-weight: 600;
  color: var(--ink-darkest);
  display: inline-flex;
  align-items: baseline;
  gap: 8px;
`;

const SummaryPercent = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: var(--ink-lighter);
`;

const ScrollArea = styled.div`
  overflow-y: auto;
  flex: 1;
  padding: 0 80px;
`;

const GroupSection = styled.div`
  margin-bottom: 24px;
`;

const GroupLabel = styled.div`
  margin-bottom: 8px;
  padding-left: 4px;
`;

const RunTable = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid var(--sky-light);
  border-radius: 10px;
  overflow: hidden;
  background: var(--base-white);
`;

const RunHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  padding: 10px 12px;
  background: var(--sky-lightest);
  border-bottom: 1px solid var(--sky-light);
`;

const RunHeaderCell = styled.div<{ $flex: number }>`
  display: inline-flex;
  align-items: center;
  flex: ${({ $flex }) => $flex};
  min-width: 0;
  padding: 0 16px;
  font-size: 11px;
  font-weight: 600;
  color: var(--ink-lighter);
  text-transform: uppercase;
  letter-spacing: 0.04em;

  &:not(:last-child) {
    border-right: 1px solid var(--sky-light);
  }
`;

const RunRow = styled.div<{ $clickable: boolean }>`
  display: flex;
  align-items: center;
  gap: 0;
  padding: 12px;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: background 0.12s;

  &:not(:last-child) {
    border-bottom: 1px solid var(--sky-light);
  }

  &:hover {
    background: ${({ $clickable }) => ($clickable ? 'var(--sky-lightest)' : 'transparent')};
  }
`;

const RunCell = styled.div<{ $flex: number }>`
  display: inline-flex;
  align-items: center;
  flex: ${({ $flex }) => $flex};
  min-width: 0;
  gap: 6px;
  padding: 0 16px;
  flex-wrap: wrap;

  &:not(:last-child) {
    border-right: 1px solid var(--sky-light);
  }
`;

const WhenStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 1px 8px;
  border-radius: 10px;
  line-height: 1;
`;

const RunExpanded = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px 16px 16px;
  margin: 2px 0 12px;
  border-radius: 8px;
  background: var(--sky-lightest);
  border: 1px solid var(--sky-light);
`;

const OutputBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const OutputHeader = styled.div`
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const Markdown = styled.div`
  font-size: 14px;
  color: var(--ink-dark);
  line-height: 1.6;

  p {
    margin: 4px 0;
  }
  ul,
  ol {
    margin: 4px 0;
    padding-left: 20px;
  }
  code {
    background: var(--flora-code-bg, rgba(0, 0, 0, 0.05));
    padding: 1px 4px;
    border-radius: 4px;
    font-size: 12px;
  }
`;

const ErrorBox = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  padding: 10px 12px;
  border-radius: 8px;
  background: rgba(211, 24, 12, 0.08);
  color: #a8160c;
`;

const DeliveryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DeliveryAttemptBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const DeliveryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DeliveryError = styled.div`
  padding: 6px 10px;
  border-radius: 6px;
  background: rgba(255, 82, 71, 0.08);
  border: 1px solid rgba(255, 82, 71, 0.2);
  margin-left: 0;
`;

const ChatLinkButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 6px;
  border: 1px solid var(--sky-light);
  background: var(--base-white);
  color: var(--ink-dark);
  font-size: 12px;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s;
  text-decoration: none;

  &:hover {
    background: var(--sky-lightest);
    border-color: var(--secondary-base);
    color: var(--secondary-base);
  }
`;

// Anchor variant of ChatLinkButton — separate from styled.button so the
// href/target attributes type-check. Visual styling kept identical.
const ChatLinkAnchor = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 6px;
  border: 1px solid var(--sky-light);
  background: var(--base-white);
  color: var(--ink-dark);
  font-size: 12px;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.12s, border-color 0.12s;

  &:hover {
    background: var(--sky-lightest);
    border-color: var(--secondary-base);
    color: var(--secondary-base);
  }
`;

const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-height: 240px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  text-align: center;
  gap: 4px;
  padding: 0 80px;
`;
